import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Loader2, Search, AlertTriangle, CheckCircle2, XCircle, TrendingUp, RefreshCw, DollarSign, User, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditExposure = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [exposureData, setExposureData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');

  useEffect(() => {
    loadCustomers();
    loadExposureData();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadExposureData = async (customerId = null) => {
    setIsLoading(true);
    try {
      const url = customerId 
        ? `${BACKEND_URL}/api/otc/credit-exposure?customer_id=${customerId}`
        : `${BACKEND_URL}/api/otc/credit-exposure`;
      const response = await axios.get(url);
      setExposureData(response.data || []);
    } catch (error) {
      console.error('Error loading credit exposure:', error);
      toast.error('Failed to load credit exposure data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadExposureData(selectedCustomer || null);
  };

  const getStatusBadge = (status, isBlocked) => {
    if (isBlocked) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          <XCircle className="w-3 h-3" /> BLOCKED
        </span>
      );
    }
    switch (status) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3" /> OK
          </span>
        );
      case 'WARNING':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            <AlertTriangle className="w-3 h-3" /> WARNING
          </span>
        );
      case 'BLOCKED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" /> OVER LIMIT
          </span>
        );
      default:
        return <span className="text-gray-500">{status}</span>;
    }
  };

  const getUtilizationBar = (percent) => {
    let bgColor = 'bg-green-500';
    if (percent >= 100) bgColor = 'bg-red-500';
    else if (percent >= 80) bgColor = 'bg-yellow-500';
    else if (percent >= 60) bgColor = 'bg-orange-500';

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${bgColor} h-2 rounded-full transition-all`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    );
  };

  // Summary cards
  const totalCustomers = exposureData.length;
  const blockedCustomers = exposureData.filter(e => e.is_blocked || e.status === 'BLOCKED').length;
  const warningCustomers = exposureData.filter(e => e.status === 'WARNING').length;
  const totalExposure = exposureData.reduce((sum, e) => sum + e.total_exposure, 0);

  return (
    <Layout>
      <div className="p-6" data-testid="credit-exposure-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Credit Exposure Report</h1>
            <p className="text-sm text-gray-600">Monitor customer credit limits and utilization</p>
          </div>
          <Button onClick={() => loadExposureData(selectedCustomer || null)} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold">{totalCustomers}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Blocked/Over Limit</p>
                <p className="text-2xl font-bold text-red-600">{blockedCustomers}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Warning</p>
                <p className="text-2xl font-bold text-yellow-600">{warningCustomers}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Exposure</p>
                <p className="text-2xl font-bold">${totalExposure.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <Select value={selectedCustomer || '__ALL__'} onValueChange={(v) => setSelectedCustomer(v === '__ALL__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </Card>

        {/* Data Table */}
        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3">Loading exposure data...</span>
            </div>
          ) : exposureData.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No credit exposure data found</p>
              <p className="text-sm">Create customers with credit limits to see data here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Credit Limit</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Open Items</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Open Orders</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Exposure</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Available</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Utilization</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {exposureData.map((item) => (
                    <tr key={item.customer_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{item.customer_id}</p>
                          <p className="text-sm text-gray-500">{item.customer_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {item.credit_limit > 0 
                          ? `$${item.credit_limit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                          : <span className="text-gray-400">Not Set</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${item.open_items.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${item.open_orders.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">
                        ${item.total_exposure.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${item.available_credit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${item.available_credit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 w-32">
                        <div className="flex items-center gap-2">
                          {getUtilizationBar(item.utilization_percent)}
                          <span className="text-xs font-mono w-12 text-right">
                            {item.utilization_percent.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(item.status, item.is_blocked)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/otc/credit-management/${item.customer_id}`)}
                          data-testid={`view-credit-${item.customer_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default CreditExposure;
